/**
 * Workspace constants and configuration
 */

/**
 * Generate user-scoped auto-save key
 * Ensures workspace isolation between different users and brokers
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier (TZ, CLEAR, XPI, RICO, SIMULATOR)
 * @returns {string} - Scoped localStorage key
 */
export const getAutoSaveKey = (user, broker) => {
  if (!user || !broker) {
    console.debug(
      "[Workspace] Missing user/broker for scoped key, falling back to global key",
    );
    return "odin-sight-workspace-autosave"; // Fallback for safety
  }
  // Sanitize user/broker to create valid localStorage key
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `odin-sight-workspace-autosave-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped workspace layout key
 * Ensures panel state isolation between different users and brokers
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getWorkspaceLayoutKey = (user, broker) => {
  if (!user || !broker) {
    console.debug(
      "[Workspace] Missing user/broker for scoped layout key, falling back to global key",
    );
    return "workspace-layout"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `workspace-layout-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped settings key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getSettingsKey = (user, broker) => {
  if (!user || !broker) {
    console.debug(
      "[Workspace] Missing user/broker for scoped settings key, falling back to global key",
    );
    return "odin-settings"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `odin-settings-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped sidebar favorites key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getSidebarFavoritesKey = (user, broker) => {
  if (!user || !broker) {
    console.debug(
      "[Workspace] Missing user/broker for scoped sidebar favorites key, falling back to global key",
    );
    return "sidebar-favorites"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `sidebar-favorites-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped favorite indicators key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getFavoriteIndicatorsKey = (user, broker) => {
  if (!user || !broker) {
    console.debug(
      "[Workspace] Missing user/broker for scoped favorite indicators key, falling back to global key",
    );
    return "favorite-indicators"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `favorite-indicators-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped drawing color key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getDrawingColorKey = (user, broker) => {
  if (!user || !broker) {
    console.debug(
      "[Workspace] Missing user/broker for scoped drawing color key, falling back to global key",
    );
    return "drawing-color-storage"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `drawing-color-storage-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped theme key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getThemeKey = (user, broker) => {
  if (!user || !broker) {
    return "theme"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `theme-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped news storage key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getNewsStorageKey = (user, broker) => {
  if (!user || !broker) {
    return "odin-news-storage"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `odin-news-storage-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped chart preference key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getChartPreferenceKey = (user, broker) => {
  if (!user || !broker) {
    return "chart-preference"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `chart-preference-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped poll key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getPollKey = (user, broker) => {
  if (!user || !broker) {
    return "odin-poll-1";
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `odin-poll-1-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped objects view mode key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getObjectsViewModeKey = (user, broker) => {
  if (!user || !broker) {
    return "objects-view-mode"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `objects-view-mode-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped sidebar view mode key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getSidebarViewModeKey = (user, broker) => {
  if (!user || !broker) {
    return "sidebar-view-mode"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `sidebar-view-mode-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped objects selected category key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getObjectsSelectedCategoryKey = (user, broker) => {
  if (!user || !broker) {
    return "objects-selected-category"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `objects-selected-category-${sanitizedBroker}-${sanitizedUser}`;
};

/**
 * Generate user-scoped watchlist key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Scoped localStorage key
 */
export const getWatchlistKey = (user, broker) => {
  if (!user || !broker) return "watchlist";
  const u = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const b = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `watchlist-${b}-${u}`;
};

/**
 * Prefix for per-desktop cache keys ("Meus Desktops" local copies)
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @returns {string} - Key prefix (desktop id is appended)
 */
export const getDesktopCachePrefix = (user, broker) => {
  if (!user || !broker) {
    return "odin-desktop-cache--"; // Fallback for safety
  }
  const sanitizedUser = user.replace(/[^a-zA-Z0-9-_]/g, "_");
  const sanitizedBroker = broker.replace(/[^a-zA-Z0-9-_]/g, "_");
  return `odin-desktop-cache-${sanitizedBroker}-${sanitizedUser}-`;
};

/**
 * Generate user-scoped per-desktop cache key
 * @param {string} user - Username/email
 * @param {string} broker - Broker identifier
 * @param {string|number} desktopId - Server desktop id (chartid)
 * @returns {string} - Scoped localStorage key
 */
export const getDesktopCacheKey = (user, broker, desktopId) =>
  `${getDesktopCachePrefix(user, broker)}${desktopId}`;

export const WORKSPACE_CONFIG = {
  // Workspace versions
  CURRENT_VERSION: "1.2",
  LEGACY_VERSION: "1.0",

  // File configuration
  FILE_EXTENSION: ".json", // Legacy - deprecated
  DESKTOP_EXTENSION: ".D26",
  TEMPLATE_EXTENSION: ".T26",
  DEFAULT_FILENAME_PREFIX: "TradeZoneCharts",
  FILE_MIME_TYPE: "application/octet-stream",
  FILE_ACCEPT: ".D26,.T26,.json,.workspace", // Support legacy formats

  // App metadata
  APP_NAME: "Odin Sight",

  // Auto-save configuration
  AUTO_SAVE_INTERVAL: 5 * 60 * 1000, // 5 minutes
  AUTO_SAVE_ACTIVITY_TIMEOUT: 10 * 60 * 1000, // 10 minutes
  AUTO_RESTORE_TIMEOUT: 60 * 60 * 1000, // 1 hour

  // File picker configuration
  FILE_PICKER_TIMEOUT: 30000, // 30 seconds
  MANUAL_INPUT_TIMEOUT: 60000, // 60 seconds

  // Remote storage
  STORAGE_KIND: "desktop",
};

/**
 * Activity events to track for auto-save
 */
export const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keypress",
  "scroll",
  "touchstart",
];

/**
 * Default panel configuration
 */
export const DEFAULT_PANEL_CONFIG = {
  routerInfoPanel: { open: true },
  logPanel: { open: true },
  routerOperationsSidebar: { open: true },
  objectsSidebar: { open: true },
};

/**
 * Workspace metadata template
 */
export const createMetadata = (description, autoSave = false) => ({
  app: WORKSPACE_CONFIG.APP_NAME,
  description,
  autoSave,
});

/**
 * Workspace snapshot template
 */
export const createWorkspaceTemplate = () => ({
  version: WORKSPACE_CONFIG.CURRENT_VERSION,
  timestamp: new Date().toISOString(),
  charts: [],
  selectedTab: 0,
  activeInteractiveTools: {},
  workspaceLayout: {
    panels: { ...DEFAULT_PANEL_CONFIG },
  },
  metadata: createMetadata("Desktop snapshot"),
});
